import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessagesSquare } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { fmtNum, fmtRelative } from '@/features/dossier/components/primitives'
import { listSubjects, type Subject } from '@/features/dossier/api'
import { listThreads, type Thread } from './api'

/**
 * The subjects this room is watching, and how loud each one is.
 *
 * ## Why this exists as a tab rather than only as a link
 *
 * The subject view was reachable only by clicking a subject tag on a thread — which works once
 * you already have threads and are already looking at one, and not at all otherwise. A room
 * watching four candidates needs a place that answers "which of them is taking fire today"
 * before it needs any individual thread.
 *
 * ## The numbers are the point, not the list
 *
 * A subject with forty unscored threads is a different morning from one with two. Both counts
 * come from the same paged endpoint the threads tab uses, asked for a single row each — the
 * totals are what is wanted, not the contents.
 */
export function SubjectsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Row[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const subjects = await listSubjects({ size: '200' })
      const counted = await Promise.all(subjects.map(async (s) => {
        // size=1 because only the totals are wanted. Asking for a page of fifty to count them
        // would move fifty rows across the wire per subject to render one number.
        const [all, unscored, latest] = await Promise.all([
          listThreads({ subjectId: s.id, size: 1 }),
          listThreads({ subjectId: s.id, scored: false, size: 1 }),
          listThreads({ subjectId: s.id, size: 1 }),
        ])
        return {
          subject: s,
          threads: all.totalElements,
          unscored: unscored.totalElements,
          newest: latest.content[0] ?? null,
        }
      }))
      // Loudest first. A subject nobody is talking about does not need the top of the list.
      setRows(counted.sort((a, b) => b.threads - a.threads))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading subjects" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Subjects"
        description="Who this room is watching. Open one to see every thread about them and to
                     ask the council a question across all of it."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <ErpTable head={<>
          <Th>Subject</Th>
          <Th>Threads</Th>
          <Th>Not scored</Th>
          <Th className="axis-hide-sm">Most recent</Th>
          <Th />
        </>}>
          {rows.length === 0 && (
            <EmptyRow colSpan={5}
                      message="No subjects yet. Create one under Dossier & Intelligence — a
                               watch cannot exist without one, because the dossier is what any
                               reply cites." />
          )}
          {rows.map((r) => (
            <tr key={r.subject.id} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/war-room/subjects/${r.subject.id}`)}>
              <Td><span className="font-medium">{r.subject.displayName}</span></Td>
              <Td><span className="text-[11px]">{fmtNum(r.threads)}</span></Td>
              <Td>
                {r.unscored > 0 ? (
                  <span className="text-[11px] text-amber-700 dark:text-amber-400">
                    {fmtNum(r.unscored)}
                  </span>
                ) : (
                  <span className="text-[11px] text-muted-foreground">none</span>
                )}
              </Td>
              <Td className="axis-hide-sm">
                <span className="text-[10px] text-muted-foreground">
                  {r.newest ? fmtRelative(r.newest.firstSeenAt) : '—'}
                </span>
              </Td>
              <Td>
                <span className="inline-flex items-center gap-1 text-[10px] text-primary">
                  <MessagesSquare className="size-3" /> Ask the council
                </span>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}

interface Row {
  subject: Subject
  threads: number
  unscored: number
  newest: Thread | null
}
